import { useRef } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';
import type {
	EXPERIMENT_REVENUE_MEMBERSHIP_IDENTIFIED_LOGIN_GATE,
	ResolvedExperimentData,
} from '@change-corgi/experiments/configs';

import { treatExperiment } from '#src/app/shared/api/experiments';
import { useLoginState } from '#src/app/shared/hooks/session';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';
import type { PaymentType } from '#src/app/shared/utils/payments';

import type { VaultLoginGateContext } from '../useRequireAuthenticationToVault';
import { useRequireAuthenticationToVault } from '../useRequireAuthenticationToVault';

type IdentifiedLoginGateExperiment = ResolvedExperimentData<typeof EXPERIMENT_REVENUE_MEMBERSHIP_IDENTIFIED_LOGIN_GATE>;

type Props = { experiment: IdentifiedLoginGateExperiment; gateContext: VaultLoginGateContext };
type GateParams = {
	email: string;
	path: string;
	googleAuthEnabled?: boolean;
	paymentType: PaymentType;
};
export type IdentifiedLoginGateOutcome = 'proceed' | 'declined' | 'blocked';

/**
 * The membership pages' vault gate for identified sessions, behind the
 * revenue_membership_identified_login_gate experiment. Authenticated and guest sessions `proceed`
 * without being treated. An identified session is treated and exposed at most once per mount, then
 * either asked to log in (`variant_1`, see useRequireAuthenticationToVault: `proceed` on login,
 * `declined` otherwise) or `blocked` (`control`, or the experiment off), for the caller to show the
 * generic payment error.
 */
export function useIdentifiedLoginGate({
	experiment,
	gateContext,
}: Props): (params: GateParams) => Promise<IdentifiedLoginGateOutcome> {
	const utilityContext = useUtilityContext();
	const loginState = useLoginState();
	const requireAuthenticationToVault = useRequireAuthenticationToVault();
	const trackExperimentExposure = useTrackExperimentExposure({ ...experiment, treated: true });
	const treatmentRef = useRef<Promise<void> | null>(null);
	const { experimentName, variation, fallback, override } = experiment;
	const isRealAssignment = !fallback && !override;

	return async ({ paymentType, ...params }) => {
		if (loginState !== 'IDENTIFIED') return 'proceed';
		if (isRealAssignment) {
			treatmentRef.current ??= (async () => {
				if (await treatExperiment(utilityContext, experimentName)) await trackExperimentExposure();
			})();
			await treatmentRef.current;
		}
		if (variation !== 'variant_1') return 'blocked';
		return (await requireAuthenticationToVault({
			...params,
			tracking: { gateContext, paymentType },
		}))
			? 'proceed'
			: 'declined';
	};
}
